import type { LineString } from 'geojson';
import { FC } from 'react';
import { MapInstance, useMap } from 'react-map-gl/maplibre';
import { ReusableComponentsVehicleModeEnum } from '../../../generated/graphql';
import { Point } from '../../../types';
import { LineRenderLayer } from '../LineRenderLayer';
import { findNearestPointOnARoad } from '../Utils/lineFromStopToInfraLink';
import { StopInfoForEditingOnMap } from './Types';

function linestringFromStop(
  stop: StopInfoForEditingOnMap | null,
): LineString | null {
  if (!stop?.closestPointOnInfraLink) {
    return null;
  }

  return {
    type: 'LineString',
    coordinates: [
      [stop.formState.longitude, stop.formState.latitude],
      stop.closestPointOnInfraLink.coordinates,
    ],
  };
}

function linestringFromDraftLocation(
  map: MapInstance | undefined,
  draftLocation: Point | null,
  vehicleMode: ReusableComponentsVehicleModeEnum | null,
): LineString | null {
  if (!draftLocation) {
    return null;
  }

  const pointOnInfraLink = findNearestPointOnARoad(
    map,
    draftLocation,
    vehicleMode,
  );

  if (!pointOnInfraLink) {
    return null;
  }

  return {
    type: 'LineString',
    coordinates: [
      [draftLocation.longitude, draftLocation.latitude],
      pointOnInfraLink.coordinates,
    ],
  };
}

type LineToClosestInfraLinkProps = {
  readonly draftLocation: Point | null;
  readonly stop: StopInfoForEditingOnMap | null;
  readonly vehicleMode: ReusableComponentsVehicleModeEnum | null;
};

export const LineToClosestInfraLink: FC<LineToClosestInfraLinkProps> = ({
  draftLocation,
  stop,
  vehicleMode,
}) => {
  const { current: map } = useMap();

  const lineToInfraLink: LineString | null =
    linestringFromStop(stop) ??
    linestringFromDraftLocation(map?.getMap(), draftLocation, vehicleMode);

  if (!lineToInfraLink) {
    return null;
  }

  return (
    <LineRenderLayer
      layerId="LineToClosestInfraLink"
      geometry={lineToInfraLink}
      layout={{
        'line-cap': 'round',
      }}
      paint={{
        'line-color': 'darkGrey',
        'line-width': 4,
        'line-opacity': 1,
        'line-offset': 0,
        'line-dasharray': [1, 1.5],
      }}
    />
  );
};
